import { useLocation, useNavigate } from "react-router-dom";

import { persistLocale } from "@/i18n/detect-locale";
import { useLocale } from "@/i18n/locale-context";
import type { LocaleCode } from "@/i18n/types";
import { LOCALES } from "@/i18n/types";
import { cn } from "@/lib/utils";

interface LocaleSwitcherProps {
  className?: string;
}

export function LocaleSwitcher({
  className,
}: LocaleSwitcherProps): JSX.Element {
  const { locale, t } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();

  const switchTo = (target: LocaleCode): void => {
    if (target === locale) return;
    const parts = location.pathname.split("/").filter(Boolean);
    const rest = parts.slice(1).join("/");
    const nextPath = rest ? `/${target}/${rest}` : `/${target}`;
    persistLocale(target);
    void navigate(nextPath + location.search + location.hash, {
      replace: false,
    });
  };

  return (
    <div
      aria-label={t("actions.switchLanguage")}
      className={cn(
        "inline-flex items-center gap-1 rounded-full border border-border bg-surface/70 p-1 text-xs font-medium",
        className,
      )}
      role="group"
    >
      {LOCALES.map((item) => {
        const isActive = item === locale;
        const label =
          item === "en"
            ? t("localeSwitcher.englishLabel")
            : t("localeSwitcher.spanishLabel");
        return (
          <button
            aria-pressed={isActive}
            className={cn(
              "rounded-full px-3 py-1 transition",
              isActive
                ? "bg-primary text-primary-foreground"
                : "text-foreground/70 hover:text-primary",
            )}
            key={item}
            onClick={() => {
              switchTo(item);
            }}
            type="button"
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
